import React, { useCallback, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";
import { Agent } from "../FeaturedAgents";
import icNewMember from "../../assets/images/ic-newmember.svg";
import icNewPartnership from "../../assets/images/ic-newpartnership.svg";
import agentIcon from "../../assets/images/agent-icon.svg";
import icInternalRelease from "../../assets/images/ic-internalrelease.svg";
import icFinancialReports from "../../assets/images/ic-financialreports.svg";
import agentIcon1 from "../../assets/images/agent_icon3.svg";
import agentIcon2 from "../../assets/images/agent_icon2.svg";
import agentIcon3 from "../../assets/images/agent_icon1.svg";
import agentIcon4 from "../../assets/images/agent_icon4.svg";
import agentIcon5 from "../../assets/images/agent_icon1.svg";
import { RootState, useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import { v4 as uuid } from "uuid";
import { Agents } from "../../types/master";

type MyAgentsProps = {
  agents: Agent[];
  onAgentClick: (agentId: number) => void;
};

const agentIcons = [agentIcon1, agentIcon2, agentIcon3, agentIcon4, agentIcon5];

// Map agent IDs to their corresponding icons
const getAgentIcon = (agentId: number): string => {
  const iconMap: Record<string, string> = {
    4: icNewMember,
    11: icNewPartnership,
    // 'm3': icInternalRelease,
    // 'm4': icFinancialReports,
  };
  return iconMap[agentId] || icNewMember; // Default to new member icon if not found
};

export default function MyAgents({
  agents,
  onAgentClick,
}: MyAgentsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const allAgents = useSelector((state: RootState) => state.agents.data);

  // Desired display order for categories in "My Agents (Beta)"
  const CATEGORY_ORDER = [
    "CEO's Office", // adjust if you want a different first category
    "Augmented HCC",
    "Marketing",
    "Procurement",
  ];

  const getCategoryRank = (category?: string | null) => {
    if (!category) return CATEGORY_ORDER.length;
    const idx = CATEGORY_ORDER.indexOf(category);
    return idx === -1 ? CATEGORY_ORDER.length : idx;
  };

  // Memoize accessible agents to avoid recalculating on every render
  const accessibleAgents = useMemo(() => {
    return allAgents
      .filter((agent) => agent.is_access === 1 && agent.id !== 501)
      .slice()
      .sort((a, b) => {
        const rankA = getCategoryRank(a.category as string);
        const rankB = getCategoryRank(b.category as string);

        if (rankA !== rankB) {
          return rankA - rankB;
        }

        // If same category rank, keep a stable, predictable secondary order
        return a.agent_name.localeCompare(b.agent_name);
      });
  }, [allAgents]);

  const handleAgentClick = useCallback(
    (agent: Agents) => {
      onAgentClick(agent.id);
    },
    [onAgentClick],
  );

  // Helper function to check if agent is available for launch
  const isAgentLaunchable = useCallback((agent: Agents): boolean => {
    // Agent IDs that should open using their endpoint URL in a new tab
    const EXTERNAL_ENDPOINT_AGENTS = [19, 31, 32, 33, 34];

    // If agent requires external endpoint but doesn't have one, it's not launchable
    if (EXTERNAL_ENDPOINT_AGENTS.includes(agent.id) && !agent.endpoint) {
      return false;
    }

    return true;
  }, []);

  return (
    <>
      <div className="card my-agents">
        <div className="card-header">
          <h3 style={{ fontFamily: "G42 Sans Black Regular" }}>My Agents</h3>
        </div>
        <p className="subtle">
          Pin your frequent workflow to get started faster
        </p>

        {accessibleAgents.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-message">
              Currently, you do not have rights to access this agent. Current
              version access provided to select SMEs.
            </p>
          </div>
        ) : (
          <div className="tiles">
            {accessibleAgents.map((agent, index) => (
              <div
                key={agent.id}
                className="tile"
                onClick={() =>
                  isAgentLaunchable(agent) && handleAgentClick(agent)
                }
                style={{
                  opacity: !isAgentLaunchable(agent) ? 0.5 : 1,
                  cursor: !isAgentLaunchable(agent) ? "not-allowed" : "pointer",
                }}
              >
                <div className="tile-icon">
                  {/* <img
                    // src={getAgentIcon(agent.id)}
                    src={`../../src/assets/images/${agent.agent_photo}`}
                    alt={agent.agent_name}
                  /> */}
                  <img
                    src={
                      agent.category === "CEO's Office"
                        ? agentIcon1 // agent_icon1.svg
                        : agent.category === "Marketing"
                          ? agentIcon2 // agent_icon2.svg
                          : agent.category === "Augmented HCC"
                            ? agentIcon3 // agent_icon3.svg
                            : agent.category === "Procurement"
                              ? agentIcon4 // agent_icon4.svg
                              : agentIcons[index % agentIcons.length]
                    }
                    alt="agent-icon"
                  />
                </div>
                <div className="tile-content">
                  <div className="tile-title">{agent.agent_name}</div>
                  <div className="tile-sub">{agent.category}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
